import i18n from "@/utils/language";

type ErrorBody = Record<string, string | string[] | Record<string, string[]>>;

/**
 * Keys the backend uses to carry machine-readable error metadata. Their values
 * are identifiers meant for logs and diagnostics, never for an end user.
 */
// Deliberately excludes the bare key `code`: some models expose a real,
// user-facing field named `code` (maintenance plans, tracker templates), and
// discarding it by key would swallow a legitimate validation message. A `code`
// whose value is an internal token is still dropped by `isMachineValue`.
const MACHINE_KEYS = new Set([
  "reason_code",
  "reasoncode",
  "required_capability",
  "requiredcapability",
  "required_permission",
  "requiredpermission",
  "error_code",
  "errorcode"
]);

/** `MISSING_CAPABILITY`, `MODULE_NOT_CONFIGURED`, ... */
const MACHINE_TOKEN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;
/** `teams.view`, `vendors.view`, `pending_issues.can_view_team_work_order`, ... */
const DOTTED_IDENTIFIER = /^[a-z0-9_]+(?:\.[a-z0-9_]+)+$/;

function isMachineValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return MACHINE_TOKEN.test(trimmed) || DOTTED_IDENTIFIER.test(trimmed);
}

function collectHumanMessages(
  obj: ErrorBody | Record<string, string[]>,
  acc: string[] = []
): string[] {
  Object.entries(obj).forEach(([key, value]) => {
    if (MACHINE_KEYS.has(key.toLowerCase())) return;
    if (typeof value === "string") {
      if (!isMachineValue(value)) acc.push(value.trim());
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (typeof entry === "string" && !isMachineValue(entry)) {
          acc.push(entry.trim());
        }
      });
      return;
    }
    if (value && typeof value === "object") {
      collectHumanMessages(value, acc);
    }
  });
  return acc;
}

/**
 * Extract the part of a backend error body that can legitimately be shown to a
 * user: human sentences, with no uppercased key prefixes and no internal
 * identifiers. Returns `undefined` when the body carries nothing but machine
 * metadata, so the caller can fall back to a translated message by status.
 */
export function humanizeErrorBody(obj: ErrorBody): string | undefined {
  const messages = collectHumanMessages(obj);
  if (!messages.length) return undefined;
  return Array.from(new Set(messages)).join(" ");
}

export class RequestError extends Error {
  status: number;
  rawData: string | ErrorBody | undefined;
  /**
   * Flattened machine representation of the error body. Diagnostics only: it
   * may contain internal codes and identifiers, so it must never reach the UI.
   */
  rawMessage: string | undefined;
  constructor(status: number, message: string | ErrorBody) {
    super();
    this.status = status;
    this.rawData = typeof message === "object" ? message : undefined;
    if (typeof message === "string") {
      this.rawMessage = message;
      this.message = message;
      return;
    }
    this.rawMessage = this.objToString(message);
    this.message =
      humanizeErrorBody(message) ?? getGenericErrorMessageByStatus(status);
  }
  objToString(obj: ErrorBody): string {
    return Object.entries(obj).reduce((acc, keyValue) => {
      return `${acc ? `${acc},` : acc} ${keyValue[0].toUpperCase()} ${
        Array.isArray(keyValue[1])
          ? keyValue[1].join(",")
          : typeof keyValue[1] === "string"
            ? keyValue[1]
            : // Bodies may carry null, number or boolean values (e.g. the
              // `lookup` of an inventory 409): only nested objects recurse.
              keyValue[1] && typeof keyValue[1] === "object"
              ? this.objToString(keyValue[1])
              : ""
      }`;
    }, "");
  }
}
/**
 * Translated message by status that does not name the resource.
 *
 * The `{{resource}}` variants below interpolate the raw request path, which
 * reads as an English token inside a Spanish sentence (`... acceder a
 * notifications unread-count .`). That is acceptable for the non-JSON branch
 * that already used it, but not for a denial the user meets routinely, so the
 * JSON fallback uses these instead.
 */
export function getGenericErrorMessageByStatus(status: number): string {
  switch (status) {
    case 401:
      return i18n.t("errors.unauthorizedNoResource");
    case 403:
      return i18n.t("errors.forbiddenNoResource");
    case 404:
      return i18n.t("errors.notFoundNoResource");
    default:
      if (status >= 500 && status < 600) {
        return i18n.t("errors.serverErrorNoResource");
      }
      return i18n.t("errors.genericNoResource");
  }
}
export function getErrorMessageByStatus(status: number, url: URL): string {
  const resource = url.pathname.replace(/(api|\/)/gi, " ");
  switch (status) {
    case 401:
      return i18n.t("errors.unauthorized", { resource });
    case 403:
      return i18n.t("errors.forbidden", { resource });
    case 404:
      return i18n.t("errors.notFound", { resource });
    default:
      if (status >= 500 && status < 600) {
        return i18n.t("errors.serverError", { resource });
      }
      return i18n.t("errors.generic", { resource });
  }
}
